import { constants } from '../common/constants/constants';
import { TOKEN } from '../common/constants/localStorage';

/**
 * Impersonation tabs (#790). A Super Admin's read-only view of another user runs in its own browser tab whose
 * session lives in that tab's sessionStorage. The sticky tab flag below makes every auth read and write in the
 * tab go to sessionStorage, so the admin's own session in localStorage is never read, sent or changed there.
 */
export const IMPERSONATION_TAB = 'impersonationTab';
export const IMPERSONATION_SESSION = 'impersonationSession';
export const HANDOFF_PATH = '/impersonate';
export const USERS_PAGE_PATH = '/impersonation';

let handoffCode = null;
let ended = false;
// once this page has been an impersonation tab it stays one until it reloads, even after the flag is cleared
let tabSeen = false;

export const isImpersonationTab = () => {
    if (tabSeen || ended) {
        return true;
    }
    try {
        tabSeen = window.sessionStorage.getItem(IMPERSONATION_TAB) === '1';
    } catch (error) {
        tabSeen = false;
    }
    return tabSeen;
};

/**
 * Runs synchronously in index.js before anything renders. On the handoff page it marks the tab and moves the
 * one-time code out of the URL fragment into memory, so it is neither kept in history nor read by any script.
 */
export const initImpersonationTab = () => {
    // a trailing slash (added by a proxy or a link rewriter) or another letter case is still the handoff page,
    // as the router matches paths case-insensitively
    if (window.location.pathname.replace(/\/+$/, '').toLowerCase() !== HANDOFF_PATH) {
        return;
    }

    // the code leaves the address bar first, whatever happens to the storage write below
    handoffCode = new URLSearchParams(window.location.hash.slice(1)).get('code');
    window.history.replaceState(null, '', HANDOFF_PATH);
    tabSeen = true;
    try {
        window.sessionStorage.setItem(IMPERSONATION_TAB, '1');
    } catch (error) {
        // blocked storage: the tab still acts as an impersonation tab until it reloads (tabSeen)
    }
};

export const takeHandoffCode = () => {
    const code = handoffCode;
    handoffCode = null;
    return code;
};

export const getImpersonationSession = () => {
    if (!isImpersonationTab()) {
        return null;
    }
    try {
        return JSON.parse(window.sessionStorage.getItem(IMPERSONATION_SESSION));
    } catch (error) {
        return null;
    }
};

/** Stores the result of POST /impersonation/exchange in this tab. There is no refresh token. */
export const startImpersonationTab = ({ accessToken, expiresAt, serverNow, target, actor }) => {
    window.sessionStorage.setItem(IMPERSONATION_TAB, '1');
    tabSeen = true;
    window.sessionStorage.setItem(TOKEN, JSON.stringify(accessToken));
    window.sessionStorage.setItem(
        IMPERSONATION_SESSION,
        JSON.stringify({ expiresAt, serverNow, receivedAt: Date.now(), target, actor })
    );
};

/** A team switch re-mints the token for the same session. */
export const replaceImpersonationToken = accessToken => {
    window.sessionStorage.setItem(TOKEN, JSON.stringify(accessToken));
};

export const isImpersonationEnded = () => ended;

/** Milliseconds left, with the local clock corrected by the server's. */
export const remainingMs = (session, now = Date.now()) => {
    const offset = new Date(session.serverNow).getTime() - session.receivedAt;
    return new Date(session.expiresAt).getTime() - (now + offset);
};

/**
 * The one way an impersonation tab ends (Stop, any 401, the countdown at zero): stop all requests, clear the
 * tab's session and reload into the Super Admin's Users page. A full reload, so no in-memory state of the
 * impersonated user survives into the admin's own session.
 */
export const endImpersonation = reason => {
    ended = true;
    try {
        window.sessionStorage.removeItem(TOKEN);
        window.sessionStorage.removeItem(IMPERSONATION_SESSION);
        window.sessionStorage.removeItem(IMPERSONATION_TAB);
    } finally {
        window.location.replace(`${constants.BASE_URL}${USERS_PAGE_PATH}?ended=${encodeURIComponent(reason)}`);
    }
};

// test hook: module state survives between tests otherwise
export const resetImpersonationModuleState = () => {
    handoffCode = null;
    ended = false;
    tabSeen = false;
};
